// Frame types and discriminants mirroring crates/wire/src/frame.rs.
//
// TypeScript uses a discriminated union keyed on `type`; variant names
// match the Rust enum variants (lower-cased tag). Byte-level values
// (`opcode`, `nonce`, `body`, `payload`, header name/value) are
// `Uint8Array` — the browser-native equivalent of Rust's `Bytes`.

/** Flag bit: final frame in this direction for the stream. */
export const FLAG_END_STREAM = 0x01;

/**
 * Maximum total payload length (including type + flags) the decoder
 * will accept. Per , receivers MUST accept up to 16 MiB.
 */
export const MAX_FRAME_BODY = 16 * 1024 * 1024;

/** Frame-type discriminants as they appear on the wire. */
export const FrameType = {
  Req: 0x01,
  Res: 0x02,
  Data: 0x03,
  End: 0x04,
  Err: 0x05,
  /**
   * Trailing HTTP headers carrying end-of-stream. Added for
   * gRPC `grpc-status` / `grpc-message` and any HTTP trailer
   * extension. TRAILERS implicitly closes the stream — peers MUST
   * NOT send a subsequent END or DATA(END_STREAM) for the same
   * stream after sending TRAILERS.
   *
   * Browser fetch() does NOT expose response trailers, so the SW
   * wrapper layer can drop them silently when forwarding a
   * tunneled response into a `Response` object — but the wire
   * codec MUST decode + re-encode them faithfully so a forwarder
   * (edge → bootstrap → SW) doesn't synthesize-or-discard data
   * its endpoints might still be peering with native peer-clients.
   */
  Trailers: 0x06,
  WsUpgrade: 0x10,
  WsAccept: 0x11,
  WsMsg: 0x12,
  WsClose: 0x13,
  Ping: 0x20,
  Pong: 0x21,
  /**
   * Runtime chunk-cap probe. Box sends a single Probe on data-
   * channel open with ~8 KiB of payload bytes; if the wrapper
   * acknowledges within the box's decision window, that session is
   * upgraded from the conservative 800-byte default cap to the
   * larger 16 KiB cap. Probe payload bytes are informational — the
   * box picks a size that exercises whatever SCTP reassembly path
   * the iOS WebKit bug touches.
   *
   * Layout: stream id is NOT used (this is a connection-level
   * frame); payload is `id: u32 BE | bytes…`. The `id` is opaque
   * to both peers — the wrapper just echoes it back in ProbeAck.
   */
  Probe: 0x30,
  /**
   * Acknowledgement of a Probe. Wrapper sends one of these
   * synchronously on receiving a Probe, echoing the probe's `id`
   * so the box can correlate against the right outstanding probe
   * if multiple are in flight.
   *
   * Layout: payload is `id: u32 BE`. No trailing bytes.
   */
  ProbeAck: 0x31,
  Goaway: 0x7f,
} as const;
export type FrameTypeByte = (typeof FrameType)[keyof typeof FrameType];

export function isKnownFrameType(v: number): v is FrameTypeByte {
  return (
    v === FrameType.Req ||
    v === FrameType.Res ||
    v === FrameType.Data ||
    v === FrameType.End ||
    v === FrameType.Err ||
    v === FrameType.Trailers ||
    v === FrameType.WsUpgrade ||
    v === FrameType.WsAccept ||
    v === FrameType.WsMsg ||
    v === FrameType.WsClose ||
    v === FrameType.Ping ||
    v === FrameType.Pong ||
    v === FrameType.Probe ||
    v === FrameType.ProbeAck ||
    v === FrameType.Goaway
  );
}

/** WebSocket opcode values as they appear in WS_MSG. Matches RFC 6455. */
export const WsOpcode = {
  Text: 0x1,
  Binary: 0x2,
  Ping: 0x9,
  Pong: 0xa,
} as const;
export type WsOpcodeByte = (typeof WsOpcode)[keyof typeof WsOpcode];

export function isKnownWsOpcode(v: number): v is WsOpcodeByte {
  return v === WsOpcode.Text || v === WsOpcode.Binary || v === WsOpcode.Ping || v === WsOpcode.Pong;
}

/** Protocol-level error codes. See  */
export const ErrorCode = {
  NO_ERROR: 0x0000,
  PROTOCOL_ERROR: 0x0001,
  INTERNAL_ERROR: 0x0002,
  FRAME_TOO_LARGE: 0x0003,
  STREAM_REFUSED: 0x0004,
  CANCEL: 0x0005,
  LOCAL_APP_DOWN: 0x0006,
  LOCAL_APP_TIMEOUT: 0x0007,
  TRANSPORT_CLOSED: 0x0008,
  UNSUPPORTED: 0x0009,
} as const;

/**
 * One HTTP-style header. Names MUST be lowercase ASCII on the wire;
 * values are opaque bytes. Duplicates (e.g. multiple `set-cookie`) are
 * preserved in insertion order.
 */
export type Header = readonly [name: Uint8Array, value: Uint8Array];
export type Headers = readonly Header[];

/** Fully-decoded frame. Discriminated union on `type`. */
export type Frame =
  | {
      type: "req";
      streamId: number;
      flags: number;
      method: Uint8Array;
      path: Uint8Array;
      headers: Headers;
    }
  | {
      type: "res";
      streamId: number;
      flags: number;
      status: number;
      headers: Headers;
    }
  | {
      type: "data";
      streamId: number;
      flags: number;
      body: Uint8Array;
    }
  | {
      type: "end";
      streamId: number;
    }
  | {
      type: "err";
      streamId: number;
      code: number;
      message: Uint8Array;
    }
  | {
      // TRAILERS — end-of-stream marker carrying trailing headers
      // (e.g., gRPC `grpc-status` / `grpc-message`). Mutually
      // exclusive with `data { END_STREAM }` and `end` for the
      // same stream — the producer chooses one terminal frame.
      type: "trailers";
      streamId: number;
      headers: Headers;
    }
  | {
      type: "ws_upgrade";
      streamId: number;
      path: Uint8Array;
      headers: Headers;
    }
  | {
      type: "ws_accept";
      streamId: number;
      headers: Headers;
    }
  | {
      type: "ws_msg";
      streamId: number;
      opcode: WsOpcodeByte;
      payload: Uint8Array;
    }
  | {
      type: "ws_close";
      streamId: number;
      code: number;
      reason: Uint8Array;
    }
  | {
      type: "ping";
      nonce: Uint8Array; // length 8
    }
  | {
      type: "pong";
      nonce: Uint8Array; // length 8
    }
  | {
      type: "goaway";
      lastAcceptedStreamId: number;
      code: number;
      message: Uint8Array;
    }
  | {
      // connection-level chunk-cap probe. Box-initiated;
      // wrapper acknowledges with `probe_ack`.
      type: "probe";
      flags: number;
      id: number;
      payload: Uint8Array;
    }
  | {
      // wrapper's response to a `probe`. `id` echoes the
      // incoming probe's id.
      type: "probe_ack";
      flags: number;
      id: number;
    }
  | {
      // forward-compat sentinel. Decoder emits this for any
      // FrameType byte it doesn't recognise; the dispatcher in the
      // translator client logs at debug and drops it instead of
      // tearing down the connection. The codec preserves
      // `typeByte` + `flags` + `payload` so a future codec
      // extension can interpret them without changing the wire.
      type: "unknown";
      typeByte: number;
      flags: number;
      payload: Uint8Array;
    };
